import React, { useMemo, useRef } from 'react';
import { injectIntlAndObserver, IRouterWithIntl } from '../../../../utils';
import { onChangeReportId } from '../../@data/actions';
import { TythonColumnsEnum } from '../../@data/store/schema/ColumnsEnum';
import { ICustomCalenderDateRange } from '../../components/CustomCalender/CustomCalender.types';
import { IReportsMetadata } from '../../DashboardCards/@data/store/schema';
import { ReportCard } from '../../DashboardCards/ReportCard/ReportCard';
import {
  constructPubCenterReportingRequestBody,
  constructTopKReportingRequestBody,
} from '../../DashboardCards/ReportCard/ReportCard.helpers';
import { ChartGrain, ReportCardType } from '../../DashboardCards/ReportCard/ReportCard.types';
import { usePubCenterReportMetadata, usePubCenterReportQuery, usePubCenterReportTopKQuery } from '../../hooks/useReportQuery';
import message from '../OverviewPage.messages';
import { ALLOWED_CHARTS, DEFAULT_COLUMN, DEFAULT_GRANULARITY, DEFAULT_METRICS } from '../utils/constants';
import { filterPTQSData } from '../utils/utils';

export interface IPubCenterReportCardProps extends IRouterWithIntl {
  dateRange: ICustomCalenderDateRange;
  reportType: ReportCardType;
}

export const PubCenterReportCard = injectIntlAndObserver((props: IPubCenterReportCardProps) => {
  const reportCardRef = useRef<HTMLDivElement>(null);

  const {
    reportType,
    dateRange,
    match,
    history,
    location,
    intl: { formatMessage },
  } = props;

  const isPTQSCard = reportType.toLocaleLowerCase().includes('ptqs');
  const isSnapshotCard = reportType.toLocaleLowerCase().includes('snapshot');
  const extraDimensions = isPTQSCard ? [TythonColumnsEnum.PTQs] : [];
  const mainDimension = DEFAULT_COLUMN[reportType] ?? TythonColumnsEnum.AccountId;
  const granularity = DEFAULT_GRANULARITY[reportType] ?? ChartGrain.NONE;
  const { data: metadata, isLoading: isMetaDataLoading } = usePubCenterReportMetadata(reportType, true);

  const topKData = isSnapshotCard
    ? filterPTQSData(
        usePubCenterReportQuery(
          constructPubCenterReportingRequestBody(reportType, reportType, dateRange, granularity, {}, [
            ...(DEFAULT_METRICS[reportType] ?? []),
            mainDimension,
            ...extraDimensions,
          ]),
          true
        ).data?.Data
      )
    : isPTQSCard
    ? usePubCenterReportQuery(
        constructPubCenterReportingRequestBody(reportType, reportType, dateRange, granularity, {}, [
          ...(DEFAULT_METRICS[reportType] ?? []),
          mainDimension,
          ...extraDimensions,
        ]),
        true
      ).data?.Data
    : usePubCenterReportTopKQuery(
        constructTopKReportingRequestBody(reportType, dateRange, granularity, [...(DEFAULT_METRICS[reportType] ?? [])]),
        mainDimension,
        extraDimensions
      ).data;

  const METADATA: IReportsMetadata = useMemo(
    () => ({
      dimensions: [
        ...(metadata?.dimensions || [
          {
            key: mainDimension,
            displayName: mainDimension,
            filterKey: mainDimension,
            incompatibleFields: [],
          },
        ]),
      ],
      metrics: [
        ...[...(DEFAULT_METRICS[reportType] ?? [])].map((metric) => ({
          key: metric,
          displayName: metric,
          filterKey: metric,
          incompatibleFields: [],
        })),
      ],
      grains: [],
      isMetadataLoaded: !isMetaDataLoading,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [metadata, reportType]
  );

  return (
    <ReportCard
      legend
      match={match}
      title={formatMessage(message[reportType])}
      dateRange={dateRange}
      history={history}
      location={location}
      reportMetadata={{ ...METADATA }}
      type={reportType}
      allowedChartTypes={ALLOWED_CHARTS[reportType] ?? []}
      allowedChartGrains={[]}
      showDetailsReportLink
      isDetailedView={false}
      filters={[]}
      reportCardRef={reportCardRef}
      dateDimension={TythonColumnsEnum.Date}
      dataMetrics={{ chartDataMetrics: `${reportType} Card ID` }}
      reportData={topKData}
      defaultColumns={[mainDimension]}
      defaultMetrics={[...(DEFAULT_METRICS[reportType] ?? [])]}
      mainDimension={mainDimension}
      reportDetailedData={topKData}
      onViewReportClick={() => {
        onChangeReportId(reportType, history);
      }}
      isPubCenterChart
      extraDimensions={extraDimensions}
      showDropdownMetrics={(DEFAULT_METRICS[reportType] ?? []).length > 1}
    />
  );
});
